import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HardHat, Activity, ClipboardCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

const CLASS_COLORS = {
  "Near Miss": "#94a3b8",
  "First Aid": "#38bdf8",
  "Medical Treatment": "#f59e0b",
  "Lost Time Injury": "#e11d48",
  "Other": "#64748b",
};

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const Panel = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="text-xs font-semibold text-slate-800 mb-3">{title}</div>
    {children}
  </div>
);

export default function ClientSafetyReview({ fy }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/hseq/client-safety-review?fy=${fy}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load the safety review"));
  }, [fy]);

  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const classes = data.classification_breakdown.map((c) => c.name);

  return (
    <div className="space-y-4" data-testid="client-safety-review">
      <div className="bg-[#003a5c] text-white rounded-lg px-5 py-4">
        <div className="text-sm font-semibold flex items-center gap-2"><HardHat className="w-4 h-4" /> Company Safety Review — {data.fy}</div>
        <div className="text-xs opacity-80 mt-1">Summary review of HSEQ performance at GBE Group — incidents, injury rates and safety audit outcomes across all operations.</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={ShieldAlert} label="Incidents (FYTD)" value={data.total_incidents}
             sub="All classifications, all sites" testid="csr-kpi-incidents" />
        <Kpi icon={Activity} label="Lost Time Injuries" value={data.lti_count}
             sub={`${data.recordable_injuries} recordable injuries`} testid="csr-kpi-lti" />
        <Kpi icon={Activity} label="TRIFR (per 1M hrs)" value={data.trifr_1m_fy}
             sub="FY to date" testid="csr-kpi-trifr" />
        <Kpi icon={ClipboardCheck} label="Safety Audits" value={data.audits_completed}
             sub={data.audit_pass_rate != null ? `${data.audit_pass_rate}% passed` : "No audits yet"} testid="csr-kpi-audits" />
      </div>

      <Panel title="Incidents by month & severity classification">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.incidents_by_month}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {classes.map((c) => (
              <Bar key={c} dataKey={c} stackId="a" fill={CLASS_COLORS[c] || "#64748b"} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="TRIFR trend (recordable injuries per 1M hours worked)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.trifr_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="trifr_1m" name="TRIFR (1M hrs)" stroke="#003a5c" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Safety audits completed & passed">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.audits_by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="completed" name="Completed" fill="#94a3b8" />
              <Bar dataKey="passed" name="Passed" fill="#059669" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}
